/**
 * Typed API client for the Nest backend. Same-origin (SPA + Nest behind ONE Cloudflare tunnel),
 * so no CORS. Sends the signed-session cookie automatically (credentials: 'include') and the
 * double-submit CSRF token on mutations. Zod-validates every response at the boundary.
 */
import { z } from 'zod';

export const bannerSchema = z.object({
  level: z.enum(['ok', 'unknown', 'degraded', 'error']),
  message: z.string(),
  staleSeconds: z.number().optional(),
  /* Optional: requiring it forced a page wanting to state a plain fact to invent an internal
     host:port as user-facing copy. A banner with no meaningful source should simply omit it. */
  source: z.string().optional(),
});
export type Banner = z.infer<typeof bannerSchema>;

export function envelopeSchema<T extends z.ZodTypeAny>(data: T) {
  return z.object({ view: z.string(), banners: z.array(bannerSchema), data });
}

export type ViewEnvelope<T> = { view: string; banners: Banner[]; data: T };

/** Typed refusal surfaced from the Nest guard chain (403/401/502). */
export class ApiRefusal extends Error {
  constructor(
    public readonly status: number,
    public readonly reason: string,
    public readonly detail?: unknown,
  ) {
    super(`api refusal ${status}: ${reason}`);
    this.name = 'ApiRefusal';
  }
}

let csrfToken: string | null = null;
export function setCsrfToken(token: string): void {
  csrfToken = token;
}

async function parseError(res: Response): Promise<never> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON error */
  }
  const reason =
    body && typeof body === 'object' && 'reason' in body ? String((body as { reason: unknown }).reason) : res.statusText;
  throw new ApiRefusal(res.status, reason, body);
}

/**
 * Read a JSON body, tolerating the EMPTY 200 Nest sends when a handler returns `null`
 * (e.g. GET /auth/session with no valid cookie: 200, Content-Length: 0). `res.json()` throws a
 * SyntaxError on that, so callers expecting a nullable payload must go through here and pair it
 * with a `.nullable()` schema.
 */
async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  return text.length === 0 ? null : JSON.parse(text);
}

/**
 * `z.ZodType<T, z.ZodTypeDef, unknown>` — three generics, not one.
 *
 * The single-generic form `z.ZodType<T>` pins the schema's INPUT type to its OUTPUT type, so any
 * schema that transforms — `.default()`, `.preprocess()`, `.catch()` — fails to satisfy it and TS
 * silently infers `T` from the input side, handing callers `unknown` or a spuriously-optional
 * field. That effectively banned input-tolerant schemas across the whole app, which is the wrong
 * default here: this SPA is served from a build that can be newer or older than the API it talks
 * to, so a schema MUST be able to accept a missing field and still hand the page a settled value.
 * Widening the input to `unknown` costs nothing — the payload really is unknown until parsed.
 */
export async function apiGet<T>(path: string, schema: z.ZodType<T, z.ZodTypeDef, unknown>): Promise<T> {
  const res = await fetch(`/api${path}`, { credentials: 'include', headers: { accept: 'application/json' } });
  if (!res.ok) await parseError(res);
  return schema.parse(await parseBody(res));
}

export async function apiPost<T>(path: string, body: unknown, schema: z.ZodType<T, z.ZodTypeDef, unknown>): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json', accept: 'application/json' };
  if (csrfToken) headers['x-csrf-token'] = csrfToken;
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) await parseError(res);
  return schema.parse(await parseBody(res));
}
